import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

const CheckIcon = () => (
  <svg className="w-6 h-6 flex-shrink-0 text-green-500" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="12" fill="currentColor" />
    <path d="M7 12.5l3 3 7-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SectionSplitComponent = ({ section, visualAlignment }) => {
  const imageOnLeft = visualAlignment === 'left';

  const imageContent = (
    <div className="w-full mx-3 lg:w-auto lg:flex-1 flex items-center justify-center order-first lg:order-none">
      {section.imageMobile?.url && (
        <img
          src={section.imageMobile.url}
          alt={section.header || 'Section image'}
          className="w-full max-w-lg rounded-2xl object-cover block lg:hidden"
        />
      )}
      {section.image?.url && (
        <img
          src={section.image.url}
          alt={section.header || 'Section image'}
          className={`w-full max-w-lg rounded-2xl object-cover ${section.imageMobile?.url ? 'hidden lg:block' : ''}`}
        />
      )}
    </div>
  );

  const textContent = (
    <div className="flex-1 mx-3 order-last lg:order-none">
      {section.header && (
        <h2 className={section.headerClasses || 'text-3xl lg:text-4xl font-bold text-[#1C2938]'}>
          {parse(section.header)}
        </h2>
      )}
      {section.body && (
        <p className={section.bodyClasses || 'text-lg font-semibold text-green-500 mt-1'}>
          {parse(section.body)}
        </p>
      )}
      {section.items?.length > 0 && (
        <div className="space-y-5 mx-2 mt-6">
          {section.items.map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="mt-1">
                {item.image?.url ? (
                  <img src={item.image.url} alt={item.header} className="w-6 h-6" />
                ) : (
                  <CheckIcon />
                )}
              </div>
              <div>
                <h3 className={item.headerClasses || 'text-base font-bold text-[#1C2938]'}>
                  {parse(item.header)}
                </h3>
                <div className="text-sm text-gray-600 mt-1">{parse(item.body)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`px-3 py-14 lg:px-24 xl:px-36 2xl:px-60 lg:py-16 w-full ${section.customClasses || ''}`}
      style={{ backgroundColor: section.backgroundColor ? section.backgroundColor : '#F3F5F6' }}
    >
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
        {imageOnLeft ? (
          <>
            {imageContent}
            {textContent}
          </>
        ) : (
          <>
            {textContent}
            {imageContent}
          </>
        )}
      </div>
    </div>
  );
};

SectionSplitComponent.propTypes = {
  section: PropTypes.shape({
    typeOf: PropTypes.string.isRequired,
    header: PropTypes.string,
    headerClasses: PropTypes.string,
    body: PropTypes.string,
    bodyClasses: PropTypes.string,
    customClasses: PropTypes.string,
    backgroundColor: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    imageMobile: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    items: PropTypes.arrayOf(PropTypes.shape({
      header: PropTypes.string.isRequired,
      headerClasses: PropTypes.string,
      body: PropTypes.string.isRequired,
      image: PropTypes.shape({
        url: PropTypes.string.isRequired,
      }),
    })),
  }).isRequired,
  visualAlignment: PropTypes.string,
};

SectionSplitComponent.defaultProps = {
  visualAlignment: 'right',
};

export default SectionSplitComponent;
